<!-- App: contains map and its filters -->
<template>
  <TooltipProvider>
    <div class="map-app">
      <div class="site-header">
        <div class="site-title"><h3>CORE Voucher Funding Map</h3></div>
        <div class="program-tabs" role="tablist" aria-label="Voucher program">
          <button
            type="button"
            :class="['program-tab', { active: activeProgram === 'core' }]"
            @click="setProgram('core')"
          >
            Heavy-Duty Equipment
          </button>

          <button
            type="button"
            :class="['program-tab', { active: activeProgram === 'sore' }]"
            @click="setProgram('sore')"
          >
            Landscaping Equipment
          </button>
        </div>
      </div>
      <main class="app-main">
        <div v-if="isPageLoading" class="page-loading-overlay">
          <div class="page-loading-card">
            <i class="fa fa-spinner fa-spin"></i>
            <span>Loading vouchers...</span>
          </div>
        </div>

        <!-- Mobile floating action button to toggle filter view -->
        <button
          type="button"
          class="filters-fab"
          @click="filtersOpen = true"
          aria-controls="filters-drawer"
          :aria-expanded="filtersOpen"
          aria-label="Open filters"
        >
          <i class="fa fa-sliders" aria-hidden="true"></i>
        </button>

        <!-- Overlay + drawer (mobile) -->
        <Transition name="filters" appear>
          <div 
            v-if="isMobile" 
            v-show="filtersOpen"
            class="filters-overlay open" 
            @click.self="filtersOpen = false"
          >
            <aside
              id="filters-drawer"
              class="filters-drawer"
              role="dialog"
              aria-modal="true"
              @keydown.esc="filtersOpen = false"
              @click.stop
              tabIndex="-1"
            >
              <div class="filters-drawer-header">
                <h3>Filters</h3>
                <button type="button" class="filters-close" @click="filtersOpen = false" aria-label="Close filters">
                  <i class="fa fa-xmark" aria-hidden="true"></i>
                </button>
              </div>

              <FiltersPanel
                :options="filterOptions"
                :program="activeProgram"
                v-model="filters"
                @reset="resetFilters"
                @geography-change="isMapUpdating = true" 
              />

            </aside>
          </div>
        </Transition>

        <section class="layout">

          <div class="panel panel-map" data-area="map">
            <KpiCards
              :vouchers="filteredVouchers"
              :program="activeProgram"
              :include-infrastructure-funding="filters.includeInfrastructureFunding"
            />
            <div class="viz-toggle-container" v-if="activeProgram !== 'sore'">
              <label class="form-label mb-0" style="line-height: 1;">Visualization Type</label>
              <div class="viz-toggle" role="group" aria-label="Visualization type">
                <button type="button" :class="{ active: activeViz === 'map' }" @click="activeViz = 'map'">
                  Voucher Funding Map
                </button>
                <button type="button" :class="{ active: activeViz === 'chart' }" @click="activeViz = 'chart'">
                  Vouchers Over Time
                </button>
              </div>
            </div>

            <div class="map-wrapper">
              <div v-show="isMapUpdating && activeViz === 'map'" class="map-loading-overlay">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Updating map...</span>
              </div>
              <MapDashboard
                v-if="activeViz === 'map'"
                ref="mapRef"
                :geojson="featureCollection"
                :vouchers="filteredVouchers"
                :geography-type="filters.geographyType"
                :program="activeProgram"
                :feature-name-prop="'label'"
                :fit-bounds-on-load="true"
                :voucher-funding-getter="(v) => getFundingAmount(v, filters.includeInfrastructureFunding)"
                @render-complete="isMapUpdating = false"
              />
              <VoucherCountByYearChart
                v-else-if="activeProgram !== 'sore'"
                :vouchers="filteredVouchers"
                :program="activeProgram"
                :include-infrastructure-funding="filters.includeInfrastructureFunding"
              />
            </div>

            <EquipmentTypeLegend v-if="activeProgram !== 'sore'" :items="equipmentLegendItems" />
          </div>
          <aside 
            v-if="!isMobile"
            id="filters-panel" 
            class="panel panel-filters desktop-filters" 
            data-area="filters" 
            :class="{ 'is-closed': !filtersOpen }">
           <FiltersPanel
              :options="filterOptions"
              :program="activeProgram"
              v-model="filters"
              @reset="resetFilters"
              @geography-change="isMapUpdating = true" 
            />
            <EquipmentTypeBreakdownChart
                :vouchers="filteredVouchers"
                :program="activeProgram"
                :include-infrastructure-funding="filters.includeInfrastructureFunding"
              />
            <EquipmentTypeLegend v-if="activeProgram === 'sore'" :items="equipmentLegendItems" />
            <div id="data-info-container">
              <div class="reporting-date">
                <small v-if="activeProgram !== 'sore'">Data current as of {{ reportingDate }}</small>
                <small v-if="activeProgram === 'sore'">Final program dataset</small>
              </div>
              <div class="download-container">
                <DownloadMenu :options="csvOptions" @select="downloadVoucherCsv" />
              </div>
            </div>
          </aside>
        </section>
      </main>
    </div>
  </TooltipProvider>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount } from 'vue'

import TooltipProvider from '@/components/TooltipProvider.vue'
import FiltersPanel from '@/components/FiltersPanel.vue'
import MapDashboard from '@/components/MapDashboard.vue'
import KpiCards from '@/components/KpiCards.vue' 
import VoucherCountByYearChart from '@/components/VoucherCountByYearChart.vue'
import EquipmentTypeBreakdownChart from '@/components/EquipmentTypeBreakdownChart.vue'
import EquipmentTypeLegend from '@/components/EquipmentTypeLegend.vue'
import DownloadMenu from '@/components/DownloadMenu.vue'

import { useVoucherData } from '@/composables/useVoucherData'
import { useGeoLayers} from '@/composables/useGeoLayers'
import { getDiscreteChartColors } from '@/utils/chartUtils'
import { getFundingAmount } from '@/utils/fundingUtils'

// import MapDashboard from '@/components/mapDashboard.vue'
// import AgencyCards from '@/components/agencyCards.vue'

// UI state 
const isMobile = ref(false)
function updateIsMobile() {
  isMobile.value = window.matchMedia("(max-width: 768px)").matches
}
onMounted(() => {
  updateIsMobile()
  window.addEventListener("resize", updateIsMobile) // Cleanup listener on unmount
})
onBeforeUnmount(() => {
  window.removeEventListener("resize", updateIsMobile)
})

const filtersOpen = ref(true)

const showDownloadMenu = ref(false)

// ------------------------------------------------
// Program selection (CORE vs SORE)
// ------------------------------------------------
const activeProgram = ref("core")

function emptyMultiFilters() {
  const next = { ...filters.value }

  for (const key of MULTI_KEYS) {
    next[key] = []
  }

  next.geographyType = "county"
  filters.value = next
}

async function setProgram(program) {
  activeProgram.value = program
  activeViz.value = "map"

  if (program === "core" && !coreVouchers.value.length) {
    await loadCore()
  }

  if (program === "sore" && !soreVouchers.value.length) {
    await loadSore()
  }

  await nextTick()

  selectAll(filterOptions.value)

  initializedByProgram.value = {
    ...initializedByProgram.value,
    [program]: true,
  }
}



// ------------------------------------------------
// DATA: Vouchers
// ------------------------------------------------
// const { vouchers, loading, error, load } = useVoucherData()
// onMounted(() => load())
const {
  vouchers: coreVouchers,
  loading: coreLoading,
  error: coreError,
  load: loadCore,
} = useVoucherData("core")

const {
  vouchers: soreVouchers,
  loading: soreLoading,
  error: soreError,
  load: loadSore,
} = useVoucherData("sore")

onMounted(() => {
  loadCore()  // fetch core vouchers

  // counties is the default view — load it first
  loadLayer("counties").then(() => {
    // after counties is done, load everything else in background
    for (const layerName of LAYERS.filter(l => l !== "counties")) {
      loadLayer(layerName)
    }
  })
})

const activeProgramDisplayName = computed(() => 
  activeProgram.value === "core"
    ? "Heavy-Duty"
    : "Landscaping"
)

const vouchers = computed(() =>
  activeProgram.value === "core"
    ? coreVouchers.value
    : soreVouchers.value
)

const loading = computed(() =>
  activeProgram.value === "core"
    ? coreLoading.value
    : soreLoading.value
)

const error = computed(() =>
  activeProgram.value === "core"
    ? coreError.value
    : soreError.value
)

const airDistrictAliases = {
  "Sacramento Metropolitan": "Sacramento Metro",
  "SCAQMD": "South Coast",
  "Yolo/Solano": "Yolo-Solano"
}

const REDEEMED_STATUSES = new Set([
  "Completed Paid"
])

const UNREDEEMED_STATUSES = new Set([
  "Accepted Pending Confirmation",
  "Approved",
  "Redemption Approved", 
  "Redemption Processing",
])

function categorizeRedemptionStatus(status) {
  if (REDEEMED_STATUSES.has(status)) return "Redeemed"
  if (UNREDEEMED_STATUSES.has(status)) return "Unredeemed"
  return "No data"
}

function parseLocalDate(dateStr) {
  if (!dateStr) return null
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed, constructs in local time
}

function normalizeCoreVoucher(v) {
  return {
    voucherRequestId: v["Voucher ID"] || "No data",
    voucherRequestYear: v["Voucher Request Submitted Year"] || "No data",
    fundingAmount: Number(v["Funding Amount"] ?? 0),
    fundingAmountWithInfrastructure: Number(v["Funding Amount (with Infrastructure)"] ?? 0),
    redemptionStatus: v["Redemption Status"] || "No data",
    equipmentType: v["Equipment Type"] || "No data",
    purchaser: v["Purchaser"] || "No data",
    manufacturer: v["Manufacturer"] || "No data",
    dealership: v["Dealership"] || "No data",
    vocationalUse: v["Vocational Use"] || "No data",
    domicileDac: v["Domicile Disadvantaged Community"] || "No data",
    // dac: v["Disadvantaged Community"] || "No data",
    smallBusiness: v["Small Business"] || "No data",
    evse: v["EVSE Created (Infrastructure)"] || "No data",
    portOperations: v["Port Operations"] || "No data",
    county: v["County"] || "No data",
    assemblyDistrict: v["Assembly District"] || "No data",
    senateDistrict: v["Senate District"] || "No data",
    airDistrict: v["Air District"] || "No data",
    censusTract: v["Census Tract"] || "No data",
    zipCode: v["Zip Code"] || "No data",
    port: v["Port"] || "No data",
    reportingDate: parseLocalDate(v["Reporting Date"]),
    // geometry: v.geometry,
    raw: v,
  }
}

function normalizeSoreVoucher(v) {
  return {
    voucherRequestId: v.voucher_request_identifier ?? "No data",
    equipmentId: v.redeem_equipments_identifier ?? "No data",
    equipmentCategory: v.equipment_category ?? "No data",
    equipmentType: v.equipment_type ?? "No data",
    redemptionStatus: v.redemption_status ?? "No data",
    voucherRequestYear: v.date_created ? new Date(v.date_created).getFullYear() : "No data",
    purchaser: v.purchaser ?? "No data",
    manufacturer: v.manufacturer ?? "No data",
    businessClassification: v.business_classification ?? "No data",
    dac: v.dac ?? "No data",
    county: v.county ?? "No data",
    assemblyDistrict: v.assembly_district ?? "No data",
    senateDistrict: v.senate_district ?? "No data",
    airDistrict: v.air_district ?? "No data",
    censusTract: v.census_tract ?? "No data",
    zipCode: v.zip_code ?? v.zip ?? "No data",
    fundingAmount: v.equipment_redeemable_amount ? Number(v.equipment_redeemable_amount) : 0,
    fundingAmountVoucher: v.actual_redeemed_per_application ? Number(v.actual_redeemed_per_application) : 0,
    // geometry: v.geometry,
    raw: v,
  }
}

const normalizedVouchers = computed(() => {
  const rows = vouchers.value ?? []

  const normalized = rows.map(v =>
    activeProgram.value === "sore"
      ? normalizeSoreVoucher(v)
      : normalizeCoreVoucher(v)
  )
  .map(v => {
    for (const key of Object.keys(v)) {
      if (v[key] === "No Data") v[key] = "No data"
    }
    // Apply air district aliases
    if (v.airDistrict && airDistrictAliases[v.airDistrict]) {
      v.airDistrict = airDistrictAliases[v.airDistrict]
    }
    return v
  })
  return normalized
})


// ------------------------------------------------
// FILTERS (keys match FiltersPanel.blocks keys)
// ------------------------------------------------
const filters = ref({
  redemptionStatus: [],    
  geographyType: "county", 
  region: [],        
  equipmentType: [],             
  equipmentCategory: [],
  voucherRequestYear: [],
  vocationalUse: [],
  purchaser: [],
  manufacturer: [],
  dealership: [],
  evse: [],
  smallBusiness: [],
  businessClassification: [],
  dac: [],
  domicileDac: [],
  portOperations: [],
  includeInfrastructureFunding: false,
})

const MULTI_KEYS = [
  "redemptionStatus",
  "region",
  "equipmentType",
  "equipmentCategory",
  "voucherRequestYear",
  "vocationalUse",
  "purchaser",
  "manufacturer",
  "dealership",
  "evse",
  "smallBusiness",
  "businessClassification",
  "dac",
  "domicileDac",
  "portOperations",
]

function selectAll(options) {
  const next = { ...filters.value }

  next.geographyType = "county"

  for (const key of MULTI_KEYS) {
    next[key] = [...(options[key] ?? [])]
  }

  filters.value = next
}
// ------------------------------------------------
// Tooltip text lookup
// ------------------------------------------------
// add later


// ------------------------------------------------
// FILTER OPTIONS (from vouchers; keys match FiltersPanel.blocks keys) 
// ------------------------------------------------
const uniqSorted = (arr) => {
  const priority = {
    "Yes": 1,
    "No": 2,
    "Other": 9997,
    "Unknown": 9998,
    "No data": 9999,
  }

  return [...new Set(arr)]
    .filter(v => v != null && v !== "")
    .sort((a, b) => {
      const pa = priority[a] ?? 50
      const pb = priority[b] ?? 50

      // If priorities differ, use that
      if (pa !== pb) return pa - pb

      // Otherwise alphabetical
      return String(a).localeCompare(String(b))
    })
}


const regionOptions = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const geo = filters.value.geographyType
  return uniqSorted(rows.map(r => r[geo]))
})


const filterOptions = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const geo = filters.value.geographyType

  return {
    redemptionStatus: uniqSorted(rows.map(r => r.redemptionStatus)),
    region: regionOptions.value,
    equipmentType: uniqSorted(rows.map(r => r.equipmentType)),
    equipmentCategory: uniqSorted(rows.map(r => r.equipmentCategory)),
    voucherRequestYear: uniqSorted(rows.map(r => r.voucherRequestYear)),
    vocationalUse: uniqSorted(rows.map(r => r.vocationalUse)),
    purchaser: uniqSorted(rows.map(r => r.purchaser)),
    manufacturer: uniqSorted(rows.map(r => r.manufacturer)),
    dealership: uniqSorted(rows.map(r => r.dealership)),
    evse: uniqSorted(rows.map(r => r.evse)),
    smallBusiness: uniqSorted(rows.map(r => r.smallBusiness)),
    businessClassification: uniqSorted(rows.map(r => r.businessClassification)),
    dac: uniqSorted(rows.map(r => r.dac)),
    domicileDac: uniqSorted(rows.map(r => r.domicileDac)),
    portOperations: uniqSorted(rows.map(r => r.portOperations)),
  }
})

// ------------------------------------------------
// DEFAULT selections (select all once vouchers are loaded)
// ------------------------------------------------
const initializedByProgram = ref({
  core: false,
  sore: false,
})

watch(
  [filterOptions, normalizedVouchers, loading, activeProgram],
  ([opts, rows, isLoading, program]) => {
    if (initializedByProgram.value[program]) return
    if (isLoading) return
    if (!rows?.length) return

    const hasAnyOptions = Object.values(opts).some(
      arr => Array.isArray(arr) && arr.length > 0
    )

    if (!hasAnyOptions) return

    selectAll(opts)

    initializedByProgram.value = {
      ...initializedByProgram.value,
      [program]: true,
    }
  },
  { immediate: true, flush: "post" }
)

// When any filter changes, set isMapUpdating to true to show spinner until new layer loads and renders
// watch(
//   filters,
//   () => {
//     isMapUpdating.value = true
//   },
//   { deep: true }
// )


// watch(
//   () => filters.value.geographyType,
//   async () => {
//     // show spinner immediately, before any other work
//     isMapUpdating.value = true
//     await nextTick()
//     await new Promise(resolve => requestAnimationFrame(resolve))

//     // then reset region
//     filters.value = {
//       ...filters.value,
//       region: [...regionOptions.value],
//     }
//   }
// )

// watch(
//   () => filters.value.geographyType,
//   async () => {
//     isMapUpdating.value = true  // show spinner immediately
//     await nextTick()
//     await new Promise(resolve => requestAnimationFrame(resolve))

//     filters.value = {
//       ...filters.value,
//       region: [...regionOptions.value],
//     }
//   }
// )
// // // // When geographyType changes, region options change → reset region to “all”
// watch(
//   () => filters.value.geographyType,
//   () => {
//     filters.value = {
//       ...filters.value,
//       region: [...regionOptions.value],
//     }
//   }
// )

// // When geographyType changes, set isMapUpdating to true to show spinner until new layer loads and renders
// watch(
//   () => filters.value.geographyType,
//   async () => {
//     isMapUpdating.value = true
//     await nextTick()
//     await new Promise(resolve => requestAnimationFrame(resolve))
//     // now the browser has had a chance to paint the spinner
//     // before the heavy geo layer loading starts
//   }
// )

// ------------------------------------------------
// FILTER vouchers
// ------------------------------------------------
const hasAny = (arr) => Array.isArray(arr) && arr.length > 0
function matchesMulti(value, selected) {
  if (!Array.isArray(selected) || selected.length === 0) return true
  return selected.includes(value)
}

const filteredVouchers = computed(() => {
  const rows = normalizedVouchers.value ?? []
  const f = filters.value

  return rows.filter(r => {
    if (!matchesMulti(r.equipmentType, f.equipmentType)) return false
    if (!matchesMulti(r.equipmentCategory, f.equipmentCategory)) return false
    if (!matchesMulti(r.redemptionStatus, f.redemptionStatus)) return false

    const regionVal = r[f.geographyType]
    if (!matchesMulti(regionVal, f.region)) return false

    if (!matchesMulti(r.voucherRequestYear, f.voucherRequestYear)) return false
    if (!matchesMulti(r.vocationalUse, f.vocationalUse)) return false
    if (!matchesMulti(r.purchaser, f.purchaser)) return false
    if (!matchesMulti(r.manufacturer, f.manufacturer)) return false
    if (!matchesMulti(r.dealership, f.dealership)) return false
    if (!matchesMulti(r.evse, f.evse)) return false
    if (!matchesMulti(r.smallBusiness, f.smallBusiness)) return false
    if (!matchesMulti(r.businessClassification, f.businessClassification)) return false
    if (!matchesMulti(r.dac, f.dac)) return false
    if (!matchesMulti(r.domicileDac, f.domicileDac)) return false
    if (!matchesMulti(r.portOperations, f.portOperations)) return false

    return true
  })
})

// ------------------------------------------------
// GEO LAYERS
// ------------------------------------------------
const isMapUpdating = ref(true) // show spinner on map until first render completes
const { loadLayer } = useGeoLayers()

const LAYER_BY_GEOGRAPHY_TYPE = {
  county: "counties",
  assemblyDistrict: "assembly_districts",
  senateDistrict: "senate_districts",
  airDistrict: "air_districts",
  censusTract: "census_tracts",
  zipCode: "zip_codes",
  port: "ports",
}

const LAYERS = Object.values(LAYER_BY_GEOGRAPHY_TYPE) // all layer names

const activeLayerName = computed(() =>
  LAYER_BY_GEOGRAPHY_TYPE[filters.value.geographyType] ?? "counties"
)
const activeGeoLayer = ref(null)

let currentLoadId = 0

watch(activeLayerName, async (layerName, oldLayerName) => {
  const loadId = ++currentLoadId
  // console.log('1. activeLayerName changed:', oldLayerName, '->', layerName, 'loadId:', loadId)

  isMapUpdating.value = true
  // console.log('2. isMapUpdating set to true')

  if (oldLayerName !== undefined) {
    await nextTick()
    // console.log('3. after nextTick')
    await new Promise(resolve => requestAnimationFrame(resolve))
    // console.log('4. after rAF, resetting region')
    filters.value = {
      ...filters.value,
      region: [...regionOptions.value],
    }
  }

  // console.log('5. calling loadLayer for', layerName)
  const layer = await loadLayer(layerName)
  // console.log('6. loadLayer done for', layerName, 'loadId:', loadId, 'currentLoadId:', currentLoadId)

  if (loadId !== currentLoadId) {
    // console.log('7. stale load, discarding', layerName)
    return
  }

  // console.log('8. setting activeGeoLayer for', layerName)
  activeGeoLayer.value = layer
}, { immediate: true })


const featureCollection = computed(() => {
  const layer = activeGeoLayer.value
  if (!layer) return null
  if (layer.type === "FeatureCollection" && Array.isArray(layer.features)) return layer
  if (Array.isArray(layer)) {
    return {
      type: "FeatureCollection",
      features: layer,
    }
  }
  return null
})


// ------------------------------------------------
// RESET
// ------------------------------------------------
async function resetFilters() {
  const opts = filterOptions.value
  for (const key of Object.keys(opts)) {
    filters.value[key] = [...(opts[key] ?? [])]
  }
  filters.value.geographyType = "county"
  filters.value.includeInfrastructureFunding = false

  await nextTick()
  filtersOpen.value = false
}

// Prevent background scroll when filters drawer is open (mobile)
watch(filtersOpen, (open) => {
  document.body.style.overflow = open ? "hidden" : ""
})
onBeforeUnmount(() => {
  document.body.style.overflow = ""
})


// ------------------------------------------------
// TOGGLE VIZ
// ------------------------------------------------
const activeViz = ref("map") // "map" or "chart"

const reportingDate = computed(() => {
  // since all vouchers have the same reporting date, we can just take the first one
  const date = normalizedVouchers.value?.[0]?.reportingDate
  return date ? date.toLocaleDateString() : "Unknown"
})

// ------------------------------------------------
// CHART: Vouchers by Year
// ------------------------------------------------
const equipmentTypes = computed(() =>
  [...new Set(
    (filteredVouchers.value ?? [])
      .map(v => v.equipmentType)
      .filter(Boolean)
  )].sort()
)

const equipmentLegendItems = computed(() => {
  const colors = getDiscreteChartColors()

  return equipmentTypes.value.map((type, index) => ({
    label: type,
    color: colors[index % colors.length],
  }))
})


// ------------------------------------------------
// SPINNER
// ------------------------------------------------
const isPageLoading = computed(() => {
  if (activeProgram.value === "core") {
    return coreLoading.value || !coreVouchers.value.length
  }
  return soreLoading.value || !soreVouchers.value.length
})


// ------------------------------------------------
// MAP RENDERING LOCK
// ------------------------------------------------

// debug
watch(() => isMapUpdating.value, (val) => {
  // console.log('isMapUpdating changed:', val)
})

// set it true immediately when geography type changes
watch(
  () => filters.value.geographyType,
  () => {
    isMapUpdating.value = true
  }
)

// ------------------------------------------------
// DOWNLOAD CSV
// ------------------------------------------------
// ------------------------------------------------
// DOWNLOAD CSV
// ------------------------------------------------
const csvOptions = [
  { value: 'full', label: 'Full Dataset (CSV)', icon: 'fa fa-file-csv' },
  { value: 'filtered', label: 'Filtered Dataset (CSV)', icon: 'fa fa-file-csv' },
]

function downloadVoucherCsv(mode = "full") {
  const rows = 
    mode === "filtered" 
      ? filteredVouchers.value 
      : normalizedVouchers.value

  if (!rows.length) return

  const exportColumns = {
     core: [
      { key: "voucherRequestId", label: "Voucher Request ID" },
      { key: "voucherRequestYear", label: "Voucher Request Year" },
      { key: "fundingAmount", label: "Funding Amount (Equipment Only)" },
      { key: "fundingAmountWithInfrastructure", label: "Funding Amount (Equipment and Infrastructure)" },
      { key: "redemptionStatus", label: "Redemption Status" },
      { key: "equipmentType", label: "Equipment Type" },
      { key: "purchaser", label: "Purchaser" },
      { key: "manufacturer", label: "Manufacturer" },
      { key: "dealership", label: "Dealership" },
      { key: "vocationalUse", label: "Vocational Use" },
      { key: "domicileDac", label: "Disadvantaged Community" },
      { key: "smallBusiness", label: "Small Business" },
      { key: "portOperations", label: "Port Operations" },
      { key: "county", label: "County" },
      { key: "airDistrict", label: "Air District" },
      { key: "assemblyDistrict", label: "Assembly District" },
      { key: "senateDistrict", label: "Senate District" },
      { key: "censusTract", label: "Census Tract" },
      { key: "zipCode", label: "Zip Code" },
      { key: "port", label: "Port" },
      { key: "reportingDate", label: "Reporting Date" },
  ],
    sore: [
      { key: "voucherRequestId", label: "Voucher Request ID" },
      { key: "equipmentId", label: "Equipment ID" },
      { key: "equipmentType", label: "Equipment Type" },
      { key: "equipmentCategory", label: "Equipment Category (Battery/Charger or Tool)" },
      { key: "fundingAmount", label: "Equipment Redeemed Amount" },
      { key: "purchaser", label: "Purchaser" },
      { key: "manufacturer", label: "Manufacturer" },
      { key: "businessClassification", label: "Business Classification" },
      { key: "dac", label: "Disadvantaged Community" },
      { key: "county", label: "County" },
      { key: "airDistrict", label: "Air District" },
      { key: "assemblyDistrict", label: "Assembly District" },
      { key: "senateDistrict", label: "Senate District" },
      { key: "zipCode", label: "Zip Code" },
      { key: "reportingDate", label: "Reporting Date" }
    ]   
  }

  const columns = exportColumns[activeProgram.value]

  const csv = [
    columns.map(c => c.label).join(","),
    ...rows.map(row =>
      columns.map(col => {
        const value = row[col.key] ?? ""

        return `"${String(value)
          .replace(/"/g, '""')
          .replace(/\n/g, " ")}"`
      }).join(",")
    )
  ].join("\n")

  const blob = new Blob([csv], {
    type: "text/csv;charset=utf-8;"
  })

  const url = URL.createObjectURL(blob)

  const link = document.createElement("a")
  link.href = url
  link.download = `${activeProgram.value}_vouchers_${mode}_${new Date().toISOString().slice(0, 10)}.csv`
  link.click()

  URL.revokeObjectURL(url)
}



</script>